import type { OutlineEntry, PDFDoc } from './types';

const COVER_PAGE_TITLE = 'Cover Page';
const PAGE_START_PREFIX = 'page_start:';

export function decodeBase64ToPdfFile(fileData: string): {
  error: null | string;
  file: { data: Uint8Array } | null;
} {
  const trimmed = fileData.trim();
  const sanitizeBase64 = (value: string) => value.replace(/\s/g, '');

  const base64Only = (() => {
    if (!trimmed.startsWith('data:')) return sanitizeBase64(trimmed);
    const match = /^data:[^;]+;base64,([\s\S]*)$/.exec(trimmed);
    return sanitizeBase64(match?.[1] ?? trimmed);
  })();

  try {
    const binaryString = atob(base64Only);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return { error: null, file: { data: bytes } };
  } catch (error) {
    console.error('Failed to decode report PDF base64:', error);
    return { error: 'Failed to decode report preview', file: null };
  }
}

export function findOutlineTitleForPage(
  entries: OutlineEntry[],
  pageNumber: number
): null | string {
  if (pageNumber === 1) return COVER_PAGE_TITLE;
  if (!entries.length) return null;
  let lo = 0;
  let hi = entries.length - 1;
  let matchIndex = -1;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const midPage = entries[mid]?.pageNumber ?? 0;
    if (midPage <= pageNumber) {
      matchIndex = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return matchIndex >= 0 ? (entries[matchIndex]?.title ?? null) : null;
}

export async function loadOutlineEntries(
  pdf: PDFDoc
): Promise<{ entries: OutlineEntry[]; outlineCount: number }> {
  const byPage = new Map<number, OutlineEntry>();
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    try {
      const page = await pdf.getPage(pageNumber);
      const textContent = await page.getTextContent();
      const lines = textContent.items
        .map(item => ('str' in item ? item.str : ''))
        .map(line => line.trim())
        .filter(line => line.length > 0);
      const markerLine = lines.find(line =>
        line.toLowerCase().includes(PAGE_START_PREFIX)
      );

      const title = (() => {
        if (markerLine) {
          const [, markerTitle = ''] = markerLine.split(PAGE_START_PREFIX);
          return markerTitle.trim();
        }
        const firstFew = lines.slice(0, 6).join(' ');
        const markerMatch = /page_start:\s*([^|]+)$/i.exec(firstFew);
        return markerMatch?.[1]?.trim() ?? '';
      })();

      if (title) {
        byPage.set(pageNumber, { pageNumber, title });
      }
    } catch {
      // Ignore pages that fail to parse text.
    }
  }

  const sortedEntries = [...byPage.values()].sort(
    (a, b) => a.pageNumber - b.pageNumber
  );
  const entries = sortedEntries.filter((entry, index) => {
    if (index === 0) return true;
    const previous = sortedEntries[index - 1];
    return previous.title !== entry.title;
  });
  const firstSlide = entries.find(entry => entry.pageNumber === 1);
  if (firstSlide) {
    firstSlide.title = COVER_PAGE_TITLE;
  } else {
    entries.unshift({ pageNumber: 1, title: COVER_PAGE_TITLE });
  }
  return { entries, outlineCount: entries.length };
}
